import { createFileRoute, useNavigate, useParams } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Clock, RefreshCw } from "lucide-react";
import {
  Alert,
  Badge,
  Button,
  ErrorPage,
  PageContent,
  PageHeader,
  PageShell,
  PageSkeleton,
} from "@h2j-data-team/ui";
import type {
  ExpressionInput,
  MasterItemChange,
  SingleAppDraft,
  VariableChange,
} from "@repo/shared";
import { ConfirmDialog, toast, useConfirmDialog } from "@/components/shared";
import { BackLink } from "@/components/back-link";
import { supportCode } from "@/components/error-screen";
import { BackupRecommendationDialog } from "@/components/shared/backup-recommendation-dialog";
import { MasterDetailEditor, type AnalysisFlags } from "@/components/single-app/MasterDetailEditor";
import {
  appAnalysisListQueryOptions,
  appAnalysisDetailQueryOptions,
} from "@/features/app-analysis/api";
import {
  singleAppDraftKeys,
  useClearSingleAppDraft,
  useReconcileSingleAppDraft,
  useSaveSingleAppDraft,
  useSingleAppDraft,
  useSyncSingleAppDraft,
} from "@/features/single-app-drafts/api";
import {
  appMetadataQueryOptions,
  connectionAppQueryOptions,
  connectionKeys,
  discardAppQueries,
  connectionQueryOptions,
  useRefreshAppMetadata,
} from "@/features/qlik-connections/api";
import { jobQueryOptions } from "@/features/jobs/api";
import { useFormatDate } from "@/hooks/use-format-date";
import { getErrorMessage } from "@/lib/api-client";
import { invalidateAppMetadataCache } from "@/lib/api/qlik-connections";
import { validateExpressions, type ExpressionCheckResult } from "@/lib/api/sync-apps";
import { getBackupAlertDismissed } from "@/lib/use-backup-alert-preference";
import {
  createPendingChangesState,
  type PendingMasterItemChange,
  type PendingMasterItemCreate,
  type PendingVariableChange,
  type PendingVariableCreate,
} from "@/stores/pending-changes";
import type { QlikApp, QlikDimension, QlikMeasure, QlikVariable } from "@repo/shared";

export const Route = createFileRoute("/_authenticated/single-app/$connectionId/apps_/$appId")({
  staticData: { pageTitle: "breadcrumbs.singleApp", breadcrumb: "breadcrumbs.editApp" },
  validateSearch: (search: Record<string, unknown>) => {
    const out: { tab?: "measures" | "dimensions" | "variables"; item?: string } = {};
    const tab = search.tab;
    if (tab === "measures" || tab === "dimensions" || tab === "variables") out.tab = tab;
    if (typeof search.item === "string") out.item = search.item;
    return out;
  },
  component: SingleAppEditPage,
});

function extractExpressionInputs(
  variableChanges: VariableChange[],
  masterItemChanges: MasterItemChange[],
): ExpressionInput[] {
  const inputs: ExpressionInput[] = [];

  for (const change of variableChanges) {
    const action = change.action ?? "update";
    if (action === "delete") continue;
    const definition = change.changes?.definition;
    if (!definition) continue;
    inputs.push({ id: change.variableId, expression: definition, kind: "numberOrExpression" });
  }

  for (const change of masterItemChanges) {
    const action = change.action ?? "update";
    if (action === "delete") continue;
    if (change.itemType === "dimension") {
      for (const fd of change.changes?.fieldDefs ?? []) {
        if (!fd.startsWith("=")) continue;
        inputs.push({ id: change.itemId, expression: fd, kind: "expression" });
      }
      if (change.changes?.labelExpression) {
        inputs.push({
          id: `${change.itemId}:labelExpression`,
          expression: change.changes.labelExpression,
          kind: "expression",
        });
      }
    } else if (change.itemType === "measure") {
      if (change.changes?.expression) {
        inputs.push({
          id: change.itemId,
          expression: change.changes.expression,
          kind: "measure",
        });
      }
      if (change.changes?.label) {
        inputs.push({
          id: `${change.itemId}:labelExpression`,
          expression: change.changes.label,
          kind: "expression",
        });
      }
    }
  }

  return inputs;
}

// Variable names and master measure titles this draft creates or renames: they only exist in
// Qlik after the save job runs, so references to them aren't bad fields. The engine resolves
// [Name] against a measure's qLabel, which the save job sets to the title.
function extractPendingNames(
  variableChanges: VariableChange[],
  masterItemChanges: MasterItemChange[],
): string[] {
  const names: string[] = [];

  for (const change of variableChanges) {
    if (change.action === "delete") continue;
    names.push(change.variableName);
    if (change.changes?.name) names.push(change.changes.name);
  }

  for (const change of masterItemChanges) {
    if (change.action === "delete" || change.itemType !== "measure") continue;
    names.push(change.changes?.title ?? change.itemTitle);
  }

  return names;
}

function upsertVariableChange(changes: VariableChange[], next: VariableChange) {
  return [...changes.filter((item) => item.variableId !== next.variableId), next];
}

function removeVariableChange(
  changes: VariableChange[],
  connectionId: string,
  appId: string,
  variableId: string,
  action: "create" | "non-create",
) {
  return changes.filter((item) => {
    if (
      item.connectionId !== connectionId ||
      item.appId !== appId ||
      item.variableId !== variableId
    ) {
      return true;
    }

    const itemAction = item.action ?? "update";
    return action === "create" ? itemAction !== "create" : itemAction === "create";
  });
}

function updateVariableCreate(
  changes: VariableChange[],
  connectionId: string,
  appId: string,
  tempId: string,
  patch: { name?: string; definition?: string; comment?: string },
) {
  return changes.map((item) => {
    if (
      item.connectionId !== connectionId ||
      item.appId !== appId ||
      item.variableId !== tempId ||
      (item.action ?? "update") !== "create"
    ) {
      return item;
    }

    const nextChanges = {
      ...item.changes,
      name: patch.name ?? item.changes.name,
      definition: patch.definition ?? item.changes.definition,
      comment: patch.comment ?? item.changes.comment,
    };

    return {
      ...item,
      variableName: nextChanges.name ?? item.variableName,
      changes: nextChanges,
    };
  });
}

function upsertMasterItemChange(changes: MasterItemChange[], next: MasterItemChange) {
  return [...changes.filter((item) => item.itemId !== next.itemId), next];
}

function removeMasterItemChange(
  changes: MasterItemChange[],
  connectionId: string,
  appId: string,
  itemId: string,
  action: "create" | "non-create",
) {
  return changes.filter((item) => {
    if (item.connectionId !== connectionId || item.appId !== appId || item.itemId !== itemId) {
      return true;
    }

    const itemAction = item.action ?? "update";
    return action === "create" ? itemAction !== "create" : itemAction === "create";
  });
}

function updateMasterItemCreate(
  changes: MasterItemChange[],
  connectionId: string,
  appId: string,
  tempId: string,
  patch: Partial<PendingMasterItemCreate>,
) {
  return changes.map((item) => {
    if (
      item.connectionId !== connectionId ||
      item.appId !== appId ||
      item.itemId !== tempId ||
      (item.action ?? "update") !== "create"
    ) {
      return item;
    }

    const nextChanges = { ...item.changes, ...patch };

    return {
      ...item,
      itemTitle: nextChanges.title ?? item.itemTitle,
      changes: nextChanges,
    };
  });
}

function SingleAppEditPage() {
  const { t } = useTranslation("single-app");
  const { t: tc } = useTranslation("common");
  const { formatDate } = useFormatDate();
  const { connectionId, appId } = useParams({
    from: "/_authenticated/single-app/$connectionId/apps_/$appId",
  });
  const navigate = useNavigate({ from: Route.fullPath });
  const { tab: urlTab, item: urlItem } = Route.useSearch();
  const queryClient = useQueryClient();
  const { confirm, dialogProps } = useConfirmDialog();
  const [backupDialogOpen, setBackupDialogOpen] = useState(false);
  // The Save button that opened the backup dialog: where the focus returns if it is cancelled.
  const saveTriggerRef = useRef<HTMLElement | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [validationErrors, setValidationErrors] = useState<ExpressionCheckResult[]>([]);

  const { isLoading: isLoadingConnection } = useQuery(connectionQueryOptions(connectionId));
  const { data: currentApp, isLoading: isLoadingApp } = useQuery({
    ...connectionAppQueryOptions(connectionId, appId),
    enabled: !!connectionId && !!appId,
    // Seed from the apps-list cache (and inherit its freshness) so navigating
    // from the app list stays free; a direct reload with no list cached falls
    // through to the single-app fetch instead of listing the whole tenant.
    initialData: () =>
      queryClient
        .getQueryData<QlikApp[]>(connectionKeys.apps(connectionId))
        ?.find((app) => app.id === appId),
    initialDataUpdatedAt: () =>
      queryClient.getQueryState(connectionKeys.apps(connectionId))?.dataUpdatedAt,
  });
  const canUpdateApp = currentApp?.canUpdate !== false;

  const {
    data: metadataCache,
    isLoading: isLoadingMetadata,
    isFetching: isFetchingMetadata,
    error: metadataError,
    refetch: refetchMetadata,
  } = useQuery({
    ...appMetadataQueryOptions(connectionId, appId),
    enabled: !!connectionId && !!appId,
  });

  const { data: latestAnalysisList } = useQuery(
    appAnalysisListQueryOptions({ connectionId, appId, limit: 1, status: "completed" }),
  );
  const latestAnalysisId = latestAnalysisList?.data?.[0]?.id;

  // getAppAnalysis já devolve AppAnalysisDetail (sem .data). appAnalysisDetailQueryOptions
  // já tem enabled: !!id, então passar "" quando não houver análise mantém a query desligada.
  const { data: analysisDetail } = useQuery(appAnalysisDetailQueryOptions(latestAnalysisId ?? ""));

  const analysisFlags = useMemo<AnalysisFlags | undefined>(() => {
    if (!analysisDetail) return undefined;
    const dimensions = new Map<string, { unused: boolean; broken: boolean; indirect: boolean }>();
    for (const d of analysisDetail.dimensions) {
      const flag = { unused: d.usedInCount === 0, broken: d.isBroken, indirect: d.isIndirectUsage };
      if (flag.unused || flag.broken || flag.indirect) dimensions.set(d.id, flag);
    }
    const measures = new Map<string, { unused: boolean; broken: boolean; indirect: boolean }>();
    for (const m of analysisDetail.measures) {
      const flag = { unused: m.usedInCount === 0, broken: m.isBroken, indirect: m.isIndirectUsage };
      if (flag.unused || flag.broken || flag.indirect) measures.set(m.id, flag);
    }
    const variables = new Map<string, { unused: boolean; broken: boolean; indirect: boolean }>();
    for (const v of analysisDetail.variables) {
      const flag = {
        unused: !v.isUsed,
        broken: !!v.expressionIssues?.errorMsg?.trim(),
        indirect: v.isIndirectUsage,
      };
      if (flag.unused || flag.broken || flag.indirect) variables.set(v.id, flag);
    }
    return { analyzedAt: analysisDetail.analyzedAt, dimensions, measures, variables };
  }, [analysisDetail]);

  const draftQuery = useSingleAppDraft(connectionId, appId);
  const saveDraftMutation = useSaveSingleAppDraft(connectionId, appId);
  const clearDraftMutation = useClearSingleAppDraft(connectionId, appId);
  const syncDraftMutation = useSyncSingleAppDraft(connectionId, appId);
  const reconcileDraftMutation = useReconcileSingleAppDraft(connectionId, appId);
  const refreshMetadataMutation = useRefreshAppMetadata();

  const activeDraftJobId = draftQuery.data?.activeJobId ?? undefined;
  const { data: activeDraftJob } = useQuery({
    ...jobQueryOptions(activeDraftJobId ?? ""),
    enabled: !!activeDraftJobId,
  });

  const prevDraftJobStatusRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    const status = activeDraftJob?.status;
    const prevStatus = prevDraftJobStatusRef.current;
    prevDraftJobStatusRef.current = status;

    if (!activeDraftJobId || !status) return;
    if (status === "pending" || status === "processing") return;
    if (!prevStatus || prevStatus === "pending" || prevStatus === "processing") {
      reconcileDraftMutation.mutateAsync(activeDraftJobId).catch((err) => {
        toast.error(getErrorMessage(err, t("edit.draftReconcileFailed")));
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeDraftJob?.status]);

  const pendingState = useMemo(
    () =>
      createPendingChangesState({
        variableChanges: draftQuery.data?.variableChanges ?? [],
        masterItemChanges: draftQuery.data?.masterItemChanges ?? [],
      }),
    [draftQuery.data?.masterItemChanges, draftQuery.data?.variableChanges],
  );

  const totalPendingCount = pendingState.totalPendingCount;
  const getAllChangesArray = useCallback(
    () => ({
      variableChanges: draftQuery.data?.variableChanges ?? [],
      masterItemChanges: draftQuery.data?.masterItemChanges ?? [],
    }),
    [draftQuery.data?.masterItemChanges, draftQuery.data?.variableChanges],
  );

  // Validation results come back keyed by draft item id; this maps them to the item's name.
  const draftItemNames = useMemo(() => {
    const names = new Map<string, string>();
    for (const change of draftQuery.data?.variableChanges ?? []) {
      names.set(change.variableId, change.changes?.name ?? change.variableName);
    }
    for (const change of draftQuery.data?.masterItemChanges ?? []) {
      names.set(change.itemId, change.changes?.title ?? change.itemTitle);
    }
    return names;
  }, [draftQuery.data?.masterItemChanges, draftQuery.data?.variableChanges]);

  const draftSaveChainRef = useRef<Promise<unknown>>(Promise.resolve());

  // Builds the next draft from the latest cached draft (read at call time, not from a
  // render-captured snapshot) and serializes saves so concurrent commits can't clobber
  // each other (last-writer-wins).
  const persistDraft = useCallback(
    (
      build: (current: {
        variableChanges: VariableChange[];
        masterItemChanges: MasterItemChange[];
      }) => { variableChanges: VariableChange[]; masterItemChanges: MasterItemChange[] },
    ) => {
      const run = async () => {
        const current =
          queryClient.getQueryData<SingleAppDraft | null>(
            singleAppDraftKeys.detail(connectionId, appId),
          ) ??
          draftQuery.data ??
          null;
        const next = build({
          variableChanges: current?.variableChanges ?? [],
          masterItemChanges: current?.masterItemChanges ?? [],
        });
        await saveDraftMutation.mutateAsync(next);
        setValidationErrors([]);
      };

      const chained = draftSaveChainRef.current.then(run, run);
      draftSaveChainRef.current = chained;
      return chained.catch((err) => {
        toast.error(getErrorMessage(err, t("edit.draftSaveFailed")));
      });
    },
    [appId, connectionId, draftQuery.data, queryClient, saveDraftMutation, t],
  );

  const clearAll = useCallback(() => {
    clearDraftMutation.mutateAsync().catch((err) => {
      toast.error(getErrorMessage(err, t("edit.draftClearFailed")));
    });
    setValidationErrors([]);
  }, [clearDraftMutation, t]);

  const isSaveBlockedByJob =
    activeDraftJob?.status === "pending" || activeDraftJob?.status === "processing";
  const canEditDraft = canUpdateApp && !isSaveBlockedByJob;

  const isLoading =
    isLoadingConnection || isLoadingApp || isLoadingMetadata || draftQuery.isLoading;
  const combinedError = metadataError ?? draftQuery.error ?? null;

  const performSaveAll = useCallback(async () => {
    if (isSaveBlockedByJob) return;

    const { variableChanges, masterItemChanges } = getAllChangesArray();
    const items = extractExpressionInputs(variableChanges, masterItemChanges);
    if (items.length > 0) {
      setIsValidating(true);
      setValidationErrors([]);
      try {
        const result = await validateExpressions(
          appId,
          connectionId,
          items,
          extractPendingNames(variableChanges, masterItemChanges),
        );
        if (!result.valid) {
          setValidationErrors(result.results.filter((r) => !r.valid));
          return;
        }
      } catch {
        toast.warning(t("edit.validation.fallbackToJob"));
      } finally {
        setIsValidating(false);
      }
    }

    try {
      const result = await syncDraftMutation.mutateAsync();
      setValidationErrors([]);

      await invalidateAppMetadataCache(connectionId, appId);
      await discardAppQueries(queryClient, connectionId, appId);
      navigate({ to: "/jobs/$id", params: { id: result.jobId } });
    } catch {
      // Error handled by mutation state.
    }
  }, [
    appId,
    connectionId,
    getAllChangesArray,
    isSaveBlockedByJob,
    navigate,
    queryClient,
    syncDraftMutation,
  ]);

  const handleSaveAllClick = useCallback(
    (trigger?: HTMLElement | null) => {
      if (isSaveBlockedByJob) return;
      if (getBackupAlertDismissed()) {
        void performSaveAll();
        return;
      }
      saveTriggerRef.current = trigger ?? null;
      setBackupDialogOpen(true);
    },
    [isSaveBlockedByJob, performSaveAll],
  );

  const handleRefreshMetadataClick = useCallback(() => {
    const runRefresh = async () => {
      if (totalPendingCount > 0) {
        await clearDraftMutation.mutateAsync();
      }
      await refreshMetadataMutation.mutateAsync({ connectionId, appId });
    };

    if (totalPendingCount === 0) {
      void runRefresh();
      return;
    }

    confirm({
      title: t("edit.refreshDraftConfirm.title"),
      description: t("edit.refreshDraftConfirm.description"),
      confirmLabel: t("edit.refreshDraftConfirm.confirm"),
      variant: "destructive",
      onConfirm: runRefresh,
    });
  }, [
    appId,
    clearDraftMutation,
    confirm,
    connectionId,
    refreshMetadataMutation,
    t,
    totalPendingCount,
  ]);

  // ---- variablesHook ----
  const variablesHook = useMemo(
    () => ({
      ...pendingState.variables,
      addChange: (change: PendingVariableChange) => {
        void persistDraft((current) => ({
          variableChanges: upsertVariableChange(current.variableChanges, {
            connectionId: change.connectionId,
            appId: change.appId,
            appName: change.appName,
            variableId: change.variableId,
            variableName: change.variableName,
            action: change.action ?? "update",
            changes: change.changes,
          }),
          masterItemChanges: current.masterItemChanges,
        }));
      },
      removeChange: (draftConnectionId: string, draftAppId: string, variableId: string) => {
        void persistDraft((current) => ({
          variableChanges: removeVariableChange(
            current.variableChanges,
            draftConnectionId,
            draftAppId,
            variableId,
            "non-create",
          ),
          masterItemChanges: current.masterItemChanges,
        }));
      },
      hasChange: (draftConnectionId: string, draftAppId: string, variableId: string) =>
        pendingState.variables.pendingChanges.has(
          `${draftConnectionId}:${draftAppId}:${variableId}`,
        ),
      getChange: (draftConnectionId: string, draftAppId: string, variableId: string) =>
        pendingState.variables.pendingChanges.get(
          `${draftConnectionId}:${draftAppId}:${variableId}`,
        ),
      clearAll,
      getChangesArray: () => draftQuery.data?.variableChanges ?? [],
      addCreate: (create: PendingVariableCreate) => {
        void persistDraft((current) => ({
          variableChanges: upsertVariableChange(current.variableChanges, {
            connectionId: create.connectionId,
            appId: create.appId,
            appName: create.appName,
            variableId: create.tempId,
            variableName: create.name,
            action: "create",
            changes: {
              name: create.name,
              definition: create.definition,
              comment: create.comment,
            },
          }),
          masterItemChanges: current.masterItemChanges,
        }));
      },
      removeCreate: (draftConnectionId: string, draftAppId: string, tempId: string) => {
        void persistDraft((current) => ({
          variableChanges: removeVariableChange(
            current.variableChanges,
            draftConnectionId,
            draftAppId,
            tempId,
            "create",
          ),
          masterItemChanges: current.masterItemChanges,
        }));
      },
      updateCreate: (
        draftConnectionId: string,
        draftAppId: string,
        tempId: string,
        changes: { name?: string; definition?: string; comment?: string },
      ) => {
        void persistDraft((current) => ({
          variableChanges: updateVariableCreate(
            current.variableChanges,
            draftConnectionId,
            draftAppId,
            tempId,
            changes,
          ),
          masterItemChanges: current.masterItemChanges,
        }));
      },
    }),
    [
      clearAll,
      draftQuery.data?.masterItemChanges,
      draftQuery.data?.variableChanges,
      pendingState.variables,
      persistDraft,
    ],
  );

  // ---- masterItemsHook ----
  const masterItemsHook = useMemo(
    () => ({
      ...pendingState.masterItems,
      addChange: (change: PendingMasterItemChange) => {
        void persistDraft((current) => ({
          variableChanges: current.variableChanges,
          masterItemChanges: upsertMasterItemChange(current.masterItemChanges, {
            connectionId: change.connectionId,
            appId: change.appId,
            appName: change.appName,
            itemId: change.itemId,
            itemType: change.itemType,
            itemTitle: change.itemTitle,
            action: change.action ?? "update",
            changes: change.changes,
          }),
        }));
      },
      removeChange: (draftConnectionId: string, draftAppId: string, itemId: string) => {
        void persistDraft((current) => ({
          variableChanges: current.variableChanges,
          masterItemChanges: removeMasterItemChange(
            current.masterItemChanges,
            draftConnectionId,
            draftAppId,
            itemId,
            "non-create",
          ),
        }));
      },
      hasChange: (draftConnectionId: string, draftAppId: string, itemId: string) =>
        pendingState.masterItems.pendingChanges.has(`${draftConnectionId}:${draftAppId}:${itemId}`),
      getChange: (draftConnectionId: string, draftAppId: string, itemId: string) =>
        pendingState.masterItems.pendingChanges.get(`${draftConnectionId}:${draftAppId}:${itemId}`),
      clearAll,
      getChangesArray: () => draftQuery.data?.masterItemChanges ?? [],
      addCreate: (create: PendingMasterItemCreate) => {
        void persistDraft((current) => ({
          variableChanges: current.variableChanges,
          masterItemChanges: upsertMasterItemChange(current.masterItemChanges, {
            connectionId: create.connectionId,
            appId: create.appId,
            appName: create.appName,
            itemId: create.tempId,
            itemType: create.itemType,
            itemTitle: create.title,
            action: "create",
            changes: {
              title: create.title,
              description: create.description,
              fieldDefs: create.fieldDefs,
              fieldLabels: create.fieldLabels,
              grouping: create.grouping,
              labelExpression: create.labelExpression,
              expression: create.expression,
              label: create.label,
              tags: create.tags,
            },
          }),
        }));
      },
      removeCreate: (draftConnectionId: string, draftAppId: string, tempId: string) => {
        void persistDraft((current) => ({
          variableChanges: current.variableChanges,
          masterItemChanges: removeMasterItemChange(
            current.masterItemChanges,
            draftConnectionId,
            draftAppId,
            tempId,
            "create",
          ),
        }));
      },
      updateCreate: (
        draftConnectionId: string,
        draftAppId: string,
        tempId: string,
        changes: Partial<PendingMasterItemCreate>,
      ) => {
        void persistDraft((current) => ({
          variableChanges: current.variableChanges,
          masterItemChanges: updateMasterItemCreate(
            current.masterItemChanges,
            draftConnectionId,
            draftAppId,
            tempId,
            changes,
          ),
        }));
      },
    }),
    [
      clearAll,
      draftQuery.data?.masterItemChanges,
      draftQuery.data?.variableChanges,
      pendingState.masterItems,
      persistDraft,
    ],
  );

  // ---- shared save-to-cache handlers ----
  const handleSaveMeasureToCache = useCallback(
    (
      measure: QlikMeasure,
      changes: {
        title?: string;
        description?: string;
        expression?: string;
        label?: string;
        tags?: string[];
        numberFormat?: string;
        color?: string;
      },
    ) => {
      masterItemsHook.addChange({
        connectionId,
        appId,
        appName: currentApp?.name,
        itemId: measure.id,
        itemType: "measure",
        itemTitle: measure.title,
        action: "update",
        changes,
        originalValues: {
          title: measure.title,
          description: measure.description,
          expression: measure.expression,
          label: measure.label,
          tags: measure.tags,
          numberFormat: measure.numberFormat,
          color: measure.color,
        },
      });
    },
    [connectionId, appId, currentApp?.name, masterItemsHook],
  );

  const handleSaveDimensionToCache = useCallback(
    (
      dimension: QlikDimension,
      changes: {
        title?: string;
        description?: string;
        fieldDefs?: string[];
        fieldLabels?: string[];
        grouping?: "N" | "H" | "C";
        labelExpression?: string;
        tags?: string[];
      },
    ) => {
      masterItemsHook.addChange({
        connectionId,
        appId,
        appName: currentApp?.name,
        itemId: dimension.id,
        itemType: "dimension",
        itemTitle: dimension.title,
        action: "update",
        changes,
        originalValues: {
          title: dimension.title,
          description: dimension.description,
          fieldDefs: dimension.fieldDefs,
          fieldLabels: dimension.fieldLabels,
          labelExpression: dimension.labelExpression,
          tags: dimension.tags,
        },
      });
    },
    [connectionId, appId, currentApp?.name, masterItemsHook],
  );

  const handleSaveVariableToCache = useCallback(
    (variable: QlikVariable, changes: { name?: string; definition?: string; comment?: string }) => {
      variablesHook.addChange({
        connectionId,
        appId,
        appName: currentApp?.name,
        variableId: variable.id,
        variableName: variable.name,
        action: "update",
        changes,
        originalValues: {
          name: variable.name,
          definition: variable.definition,
          description: variable.description,
        },
      });
    },
    [connectionId, appId, currentApp?.name, variablesHook],
  );

  const handleDeleteMeasureToCache = useCallback(
    (measure: QlikMeasure) => {
      masterItemsHook.addChange({
        connectionId,
        appId,
        appName: currentApp?.name,
        itemId: measure.id,
        itemType: "measure",
        itemTitle: measure.title,
        action: "delete",
        changes: {},
        originalValues: {
          title: measure.title,
          description: measure.description,
          expression: measure.expression,
          label: measure.label,
          tags: measure.tags,
          numberFormat: measure.numberFormat,
          color: measure.color,
        },
      });
    },
    [connectionId, appId, currentApp?.name, masterItemsHook],
  );

  const handleDeleteDimensionToCache = useCallback(
    (dimension: QlikDimension) => {
      masterItemsHook.addChange({
        connectionId,
        appId,
        appName: currentApp?.name,
        itemId: dimension.id,
        itemType: "dimension",
        itemTitle: dimension.title,
        action: "delete",
        changes: {},
        originalValues: {
          title: dimension.title,
          description: dimension.description,
          fieldDefs: dimension.fieldDefs,
          fieldLabels: dimension.fieldLabels,
          labelExpression: dimension.labelExpression,
          tags: dimension.tags,
        },
      });
    },
    [connectionId, appId, currentApp?.name, masterItemsHook],
  );

  const handleDeleteVariableToCache = useCallback(
    (variable: QlikVariable) => {
      variablesHook.addChange({
        connectionId,
        appId,
        appName: currentApp?.name,
        variableId: variable.id,
        variableName: variable.name,
        action: "delete",
        changes: {},
        originalValues: {
          name: variable.name,
          definition: variable.definition,
          description: variable.description,
        },
      });
    },
    [connectionId, appId, currentApp?.name, variablesHook],
  );

  if (isLoading) {
    return <PageSkeleton variant="detail" label={t("edit.loading")} />;
  }

  const header = (
    <PageHeader
      title={currentApp?.name || t("edit.application")}
      description={t("edit.editVariables")}
      back={
        <BackLink
          to="/single-app/$connectionId/apps"
          params={{ connectionId }}
          label={t("edit.backToApps")}
        />
      }
      meta={
        <>
          {metadataCache?.fetchedAt && (
            <span className="hidden items-center gap-1 text-[12px] text-faint lg:flex">
              <Clock className="h-3 w-3" aria-hidden />
              {formatDate(metadataCache.fetchedAt)}
            </span>
          )}
          {!canEditDraft && <Badge variant="secondary">{t("edit.readOnly")}</Badge>}
        </>
      }
      actions={
        <Button
          variant="outline"
          size="sm"
          onClick={handleRefreshMetadataClick}
          loading={refreshMetadataMutation.isPending || isFetchingMetadata}
          disabled={clearDraftMutation.isPending}
          aria-label={t("edit.updateCache")}
          title={t("edit.updateCacheTitle")}
        >
          <RefreshCw />
          <span className="hidden sm:inline">{t("edit.updateCache")}</span>
        </Button>
      }
    />
  );

  // The editor needs both the metadata cache and the draft: if either fails there is
  // nothing to edit. The server's message stays out of the page; a 5xx gets a support code.
  if (combinedError) {
    return (
      <ErrorPage
        kind="load-failed"
        header={header}
        title={t("edit.loadFailed")}
        description={t("edit.loadFailedDescription")}
        supportCode={supportCode(combinedError, tc("error.supportCode"))}
        actions={
          <Button
            variant="outline"
            onClick={() => {
              void refetchMetadata();
              void draftQuery.refetch();
            }}
          >
            {tc("tryAgain")}
          </Button>
        }
      />
    );
  }

  const notices = [
    !canUpdateApp && (
      <Alert key="read-only" tone="warning" title={t("edit.readOnlyWarning.title")}>
        {t("edit.readOnlyWarning.description")}
      </Alert>
    ),
    isSaveBlockedByJob && (
      <Alert key="blocked" tone="info">
        {t("edit.saveBlockedByActiveJob")}
      </Alert>
    ),
    syncDraftMutation.error && (
      <Alert key="job-failed" tone="danger">
        {t("edit.jobFailed", { error: getErrorMessage(syncDraftMutation.error) })}
      </Alert>
    ),
    validationErrors.length > 0 && (
      <Alert key="validation" tone="danger" title={t("edit.validation.title")}>
        <ul className="space-y-1">
          {validationErrors.map((r) => (
            <li key={r.id}>
              <span className="font-medium">
                {draftItemNames.get(r.id.replace(/:labelExpression$/, "")) ?? r.id}
              </span>
              {r.errorMsg && <span>: {r.errorMsg}</span>}
              {r.badFieldNames.length > 0 && (
                <span>
                  {" "}
                  - {t("edit.validation.badFields")}: {r.badFieldNames.join(", ")}
                </span>
              )}
              {r.dangerousFieldNames.length > 0 && (
                <span>
                  {" "}
                  - {t("edit.validation.dangerousFields")}: {r.dangerousFieldNames.join(", ")}
                </span>
              )}
            </li>
          ))}
        </ul>
      </Alert>
    ),
  ].filter(Boolean);

  return (
    <PageShell header={header}>
      {/* The editor fills what is left of the page: only its panels scroll. */}
      <PageContent fill className="gap-4">
        {notices.length > 0 && <div className="flex flex-col gap-3">{notices}</div>}

        {/* Master / Detail editor body */}
        <MasterDetailEditor
          connectionId={connectionId}
          appId={appId}
          currentApp={currentApp}
          canUpdateApp={canEditDraft}
          measures={metadataCache?.measures}
          dimensions={metadataCache?.dimensions}
          variables={metadataCache?.variables}
          fields={metadataCache?.fields}
          masterItemsHook={masterItemsHook}
          variablesHook={variablesHook}
          handleSaveMeasureToCache={handleSaveMeasureToCache}
          handleSaveDimensionToCache={handleSaveDimensionToCache}
          handleSaveVariableToCache={handleSaveVariableToCache}
          handleDeleteMeasureToCache={handleDeleteMeasureToCache}
          handleDeleteDimensionToCache={handleDeleteDimensionToCache}
          handleDeleteVariableToCache={handleDeleteVariableToCache}
          initialTab={urlTab}
          initialItem={urlItem}
          onSelectionChange={(tab, item) =>
            navigate({
              search: (prev) => ({
                ...prev,
                tab: tab === "dimensions" ? undefined : tab,
                item: item ?? undefined,
              }),
              replace: true,
            })
          }
          pendingCount={totalPendingCount}
          onSaveAll={handleSaveAllClick}
          onDiscardAll={clearAll}
          isSaving={syncDraftMutation.isPending || isValidating}
          saveDisabled={
            !canUpdateApp || syncDraftMutation.isPending || isValidating || isSaveBlockedByJob
          }
          analysisFlags={analysisFlags}
        />
      </PageContent>

      <BackupRecommendationDialog
        open={backupDialogOpen}
        onOpenChange={setBackupDialogOpen}
        onConfirm={performSaveAll}
        isPending={syncDraftMutation.isPending}
        restoreFocusRef={saveTriggerRef}
      />
      <ConfirmDialog {...dialogProps} />
    </PageShell>
  );
}
